import type { ReferencedAppSettingsRaw } from "@certa/studio-core";
import { ChangeLevel } from "@certa/queries/queries/designAI/getInteractionFulfillment.query";
import { dbOps } from "../../hooks/dbOps.store";
import {
  AIIntents,
  Intents
} from "@certa/studio-core/src/constants/activityLogs.constants";
import { studioState } from "../../state";
import { appSettingsModelCreator } from "@certa/queries/models/appSettings.model";
import type { CommandAIResponseOutputChange } from "@certa/queries/queries/designAI/experimental/conversation/v4/getConversationHistory.query";
import unset from "lodash-es/unset";
import set from "lodash-es/set";
import { getAppJSON } from "@certa/queries/queries/appJSON/getAppJSON.ts";
import { processModelCreatorFromAppJSON } from "../../../../models/process.model";
import type { ProcessRawAppJSON } from "@certa/queries/schemas/studio/appProcessJSON.schema";
import type { DataObjectAppJSON } from "@certa/types/src/dataObjects.types";
import { recordsMegaJSONModelCreatorFromAppJSON } from "../../../../models/app.model";

export const importJSONChangesV3 = async (props: {
  changes: CommandAIResponseOutputChange[];
  branchSavepointId: number;
}) => {
  const { changes, branchSavepointId } = props;
  // Storing data in buffer maps, which will be applied to DB in the end using dbOps handlers
  // Ensures retrieval of up-to-date data and maintains transaction integrity.
  let appSettings = studioState().getAppSettingsRaw();

  const processesToUpdate = new Set<string>();
  let hasDataObjectChanges = false;
  // Apply changes
  for (const change of changes) {
    // =================== APP SETTINGS ===================
    if (change.level === "app_settings") {
      if (change.op === "delete") {
        if (isPathEndWithNumber(change.path)) {
          const { settingKey, index } =
            getAppSettingsDynamicValueFromPath(change.path) ?? {};
          if (!settingKey || index === undefined) {
            throw new Error(`Invalid path: ${change.path}`);
          }
          appSettings = {
            ...appSettings,
            [settingKey]: (appSettings[settingKey] ?? []).filter(
              (_, i: number) => i !== index
            )
          };
        } else {
          unset(appSettings, change.path);
        }
      } else {
        set(appSettings, change.path, change.value);
      }
    } else if (change.level === "records") {
      hasDataObjectChanges = true;
    } else {
      const processId = getProcessIdFromPath(change.path)?.toString();
      if (processId) {
        processesToUpdate.add(processId);
      }
    }
  }
  const paths = [...processesToUpdate.values()].map(
    processId => `processes.${processId}`
  );
  if (hasDataObjectChanges) {
    paths.push("records");
  }

  const appJSON: Record<string, ProcessRawAppJSON | null | undefined> & {
    records: DataObjectAppJSON[] | null | undefined;
  } =
    paths.length > 0
      ? await getAppJSON({
          branchSavepointId,
          paths
        })
      : undefined;

  dbOps().transaction(
    () => {
      paths.forEach(path => {
        const processId = getProcessIdFromPath(path)?.toString();
        if (processId && !!appJSON[path]) {
          const process = processModelCreatorFromAppJSON(appJSON[path], {
            processId
          });
          if (process) {
            dbOps().replaceProcess(processId, process);
          }
        } else if (path === "records" && appJSON.records) {
          const records = appJSON.records;
          if (records && Array.isArray(records)) {
            dbOps().replaceDataObjects(
              recordsMegaJSONModelCreatorFromAppJSON(records)
            );
          }
        }
      });

      dbOps().replaceAllAppSettings(
        appSettingsModelCreator({
          ...appSettings,
          file_paths: []
        })
      );
    },
    {
      aiIntents: changes.reduce((aiIntents: AIIntents[], change) => {
        const intent = getAIIntent(change);
        if (intent) {
          aiIntents.push(intent);
        }
        return aiIntents;
      }, []),
      intent: Intents.DESIGN_AI_PROMPT,
      skipLoggingInLedgerAndUndoStack: true
    }
  );
};

const aiIntentMapper: Record<
  CommandAIResponseOutputChange["op"],
  Record<ChangeLevel, AIIntents>
> = {
  create: {
    [ChangeLevel.PROCESS]: AIIntents.CREATE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.CREATE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.CREATE_STEP,
    [ChangeLevel.FIELD]: AIIntents.CREATE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.CREATE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.CREATE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.CREATE_DATA_OBJECT
  },
  update: {
    [ChangeLevel.PROCESS]: AIIntents.UPDATE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.UPDATE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.UPDATE_STEP,
    [ChangeLevel.FIELD]: AIIntents.UPDATE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.UPDATE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.UPDATE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.UPDATE_DATA_OBJECT
  },
  delete: {
    [ChangeLevel.PROCESS]: AIIntents.DELETE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.DELETE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.DELETE_STEP,
    [ChangeLevel.FIELD]: AIIntents.DELETE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.DELETE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.DELETE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.DELETE_DATA_OBJECT
  }
};

const getIsChangeAtEventRuleRule = (path: string | undefined) => {
  if (!path) {
    return false;
  }
  // Match pattern like processes.<number>.event_rules.<number>.rules.<number>
  return /^processes\.\d+\.event_rules\.\d+\.rules\.\d+/.test(path);
};
const getIsChangeAtEventRuleEvent = (path: string | undefined) => {
  if (!path) {
    return false;
  }
  // Match pattern like processes.<number>.event_rules.<number>.events.<number>
  return /^processes\.\d+\.event_rules\.\d+\.events\.\d+/.test(path);
};

const getAIIntent = (change: CommandAIResponseOutputChange) => {
  const intent = aiIntentMapper[change.op][change.level];
  if (change.path) {
    const isChangeAtEventRuleRule = getIsChangeAtEventRuleRule(change.path);
    const isChangeAtEventRuleEvent = getIsChangeAtEventRuleEvent(change.path);

    if (intent === AIIntents.CREATE_EVENT_RULE) {
      if (isChangeAtEventRuleEvent) {
        return AIIntents.CREATE_EVENT_RULE_EVENT;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.CREATE_EVENT_RULE_RULE;
      }
      return AIIntents.CREATE_EVENT_RULE;
    } else if (intent === AIIntents.UPDATE_EVENT_RULE) {
      if (isChangeAtEventRuleEvent) {
        return AIIntents.UPDATE_EVENT_RULE_EVENT;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.UPDATE_EVENT_RULE_RULE;
      }
      return AIIntents.UPDATE_EVENT_RULE;
    } else if (intent === AIIntents.DELETE_EVENT_RULE) {
      if (isChangeAtEventRuleEvent) {
        return AIIntents.DELETE_EVENT_RULE_EVENT;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.DELETE_EVENT_RULE_RULE;
      }
      return AIIntents.DELETE_EVENT_RULE;
    }
  }
  return intent;
};

export const isPathEndWithNumber = (path: string | undefined) => {
  if (!path) {
    return false;
  }
  return /\d+$/.test(path);
};

function isAppSettingsItemType(
  itemType: string
): itemType is keyof ReferencedAppSettingsRaw {
  return (
    itemType === "emailtemplate" ||
    itemType === "emailattachment" ||
    itemType === "group" ||
    itemType === "businessunit" ||
    itemType === "region" ||
    itemType === "extrajson" ||
    itemType === "workflowkind" ||
    itemType === "workflowstatus" ||
    itemType === "workflowpdftemplate" ||
    itemType === "alertcategory" ||
    itemType === "commentflagoption" ||
    itemType === "objecttypeworkflowkindrelation" ||
    itemType === "integrationconfig" ||
    itemType === "integrationtoken"
  );
}

const getAppSettingsDynamicValueFromPath = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like app_settings.<settingKey>.<index>
  const match = path.match(/app_settings\.(\w+)\.(\d+)/);
  if (match && match[1] && match[2]) {
    return {
      settingKey: isAppSettingsItemType(match[1])
        ? match[1]
        : (() => {
            throw new Error(`Invalid app settings item type: ${match[1]}`);
          })(),
      index: parseInt(match[2], 10)
    };
  }
  return undefined;
};

export const getProcessIdFromPath = (
  path: string | undefined
): number | undefined => {
  if (!path) {
    return undefined;
  }
  // Match pattern like processes.<number>
  const processId = path.match(/processes\.(\d+)/)?.[1];
  if (!processId) {
    return undefined;
  }
  return parseInt(processId, 10);
};

const getStepgroupDetailsFromDBForSwimlanePath = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like processes.<number>.stepgroups.<number>
  const matches = path.match(/processes\.(\d+)\.stepgroups\.(\d+)/);
  if (!matches) {
    return undefined;
  }
  const processId = matches[1];
  const stepgroupIndex = matches[2];
  if (!processId || !stepgroupIndex) {
    return undefined;
  }
  const process = studioState().getProcess(processId);
  if (!process) {
    return undefined;
  }
  const stepgroupId = process.swimlaneSequence[Number(stepgroupIndex)];
  if (stepgroupId === undefined) {
    return undefined;
  }
  const stepgroup = studioState().getSwimlane(processId, stepgroupId);
  if (!stepgroup) {
    return undefined;
  }
  return {
    processId: Number(processId),
    stepgroupId,
    tag: stepgroup.tag
  };
};

const getStepDetailsFromDBForStepPath = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like processes.<number>.stepgroups.<number>.steps.<number>
  const matches = path.match(
    /processes\.(\d+)\.stepgroups\.(\d+)\.steps\.(\d+)/
  );
  if (!matches) {
    return undefined;
  }
  const processId = matches[1];
  const stepgroupIndex = matches[2];
  const stepIdIndex = matches[3];
  if (!processId || !stepgroupIndex || !stepIdIndex) {
    return undefined;
  }
  const process = studioState().getProcess(processId);
  if (!process) {
    return undefined;
  }
  const stepgroupId = process.swimlaneSequence[Number(stepgroupIndex)];
  if (stepgroupId === undefined) {
    return undefined;
  }
  const stepId = studioState().getSwimlane(processId, stepgroupId).steps[
    Number(stepIdIndex)
  ];
  if (stepId === undefined) {
    return undefined;
  }
  const step = studioState().getStep(processId, stepId);
  if (!step) {
    return undefined;
  }
  return {
    processId: Number(processId),
    stepgroupId,
    stepId,
    tag: step.tag
  };
};

const getFieldDetailsFromDBForFieldPath = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like processes.<number>.stepgroups.<number>.steps.<number>.fields.<number>
  const matches = path.match(
    /processes\.(\d+)\.stepgroups\.(\d+)\.steps\.(\d+)\.fields\.(\d+)/
  );
  if (!matches) {
    return undefined;
  }
  const processId = matches[1];
  const stepgroupIndex = matches[2];
  const stepIdIndex = matches[3];
  const fieldIdIndex = matches[4];
  if (!processId || !stepgroupIndex || !stepIdIndex || !fieldIdIndex) {
    return undefined;
  }
  const process = studioState().getProcess(processId);
  if (!process) {
    return undefined;
  }
  const stepgroupId = process.swimlaneSequence[Number(stepgroupIndex)];
  if (stepgroupId === undefined) {
    return undefined;
  }
  const stepId = studioState().getSwimlane(processId, stepgroupId).steps[
    Number(stepIdIndex)
  ];
  if (stepId === undefined) {
    return undefined;
  }
  const fieldId = studioState().getStep(processId, stepId).fields[
    Number(fieldIdIndex)
  ];
  if (fieldId === undefined) {
    return undefined;
  }
  const field = studioState().getField(processId, fieldId);
  if (!field) {
    return undefined;
  }
  return {
    processId: Number(processId),
    stepgroupId,
    stepId,
    fieldId,
    tag: field.tag
  };
};

export const getComponentDetailsFromDBForChange = (
  change: CommandAIResponseOutputChange
) => {
  if (change.level === ChangeLevel.STEP) {
    return getStepDetailsFromDBForStepPath(change.path);
  } else if (change.level === ChangeLevel.SWIMLANE) {
    return getStepgroupDetailsFromDBForSwimlanePath(change.path);
  } else if (change.level === ChangeLevel.FIELD) {
    return getFieldDetailsFromDBForFieldPath(change.path);
  }
};
